import React from 'react';
import { Sun, Moon, SunMoon, MoonStar } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/context/ThemeContext';

/* ---------------------------------------------------------------------------
 * ThemeToggle — Light / Dark / Hybrid / Hybrid Dark segmented control.
 *
 *   Light        cream chrome + cream canvas + cream cards
 *   Dark         gold-on-black everywhere
 *   Hybrid       dark chrome + cream canvas + cream cards
 *   Hybrid Dark  dark chrome + cream canvas + dark cards
 *
 * variant="segmented" (default): full pill with labels, for Settings.
 * variant="compact": icon-only, for tight spots like the top bar.
 * ------------------------------------------------------------------------- */
const OPTIONS = [
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
  { value: 'hybrid', label: 'Hybrid', Icon: SunMoon },
  { value: 'hybrid-dark', label: 'Hybrid Dark', Icon: MoonStar },
];

export const ThemeToggle = ({ variant = 'segmented', className }) => {
  const { theme, setTheme } = useTheme();
  const compact = variant === 'compact';

  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      className={cn(
        'inline-flex items-center rounded-lg border border-border bg-[hsl(var(--muted))]/60 p-0.5',
        // One row of four: wrapping turned it into a 2x2 block at tablet width.
        compact && 'flex-nowrap',
        className
      )}
      data-testid="theme-toggle"
    >
      {OPTIONS.map(({ value, label, Icon }) => {
        const active = theme === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={active}
            title={label}
            onClick={() => setTheme(value)}
            data-testid={`theme-option-${value}`}
            className={cn(
              'inline-flex items-center justify-center gap-1.5 rounded-md transition-colors',
              compact ? 'h-7 w-7' : 'h-8 px-3',
              active
                ? 'bg-[hsl(var(--card))] text-foreground shadow-sm ring-1 ring-border'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <Icon size={compact ? 15 : 14} />
            {!compact ? (
              <span className="text-xs font-medium uppercase tracking-[0.12em]">
                {label}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
};

export default ThemeToggle;
